import React, {useReducer} from 'react'
import {Badge, Card, Colors, Icon, IconButton, P, S1} from 'src/components/UI'
import {Order, OrderAddress, Reseller} from 'types/types.js'
import ViewOrderCollo from './ViewOrderCollo'
import {getOriginalAddressColli} from '@utils/retourAddresses'

type ViewOrderAddressProps = {
    order: Order
    address: OrderAddress
    onClickEdit?: (index: number) => void
    onClickPrintLabels?: (orderId: string, addressIndex?: number, colloIndex?: number) => void
    reseller: Reseller
    // An address that is no longer part of the order (the original address of a retour), shown read-only
    disabled?: boolean
    isRetour?: boolean
}

type ViewOrderAddressState = {
    hover: boolean
}

const ViewOrderAddress = ({order, address, onClickEdit, onClickPrintLabels, reseller, disabled, isRetour}: ViewOrderAddressProps) => {
    const [state, setState] = useReducer((s: ViewOrderAddressState, a: ViewOrderAddressState) => a, {hover: false})
    const {hover} = state

    // An original address of a retour shows the status it ended with, its colli now belong to the retour address
    const addressColli = disabled ? getOriginalAddressColli(order, address) : order.colli.filter((collo) => (address.type === 'pickup' && collo.pickupAddressIndex === address.index) || (address.type === 'delivery' && collo.deliveryAddressIndex === address.index))

    return (
        <Card style={{padding: 0, marginBottom: 12, background: disabled ? Colors.backgroundNeutral2 : undefined, opacity: disabled ? 0.7 : 1}}>
            <div style={{display: 'flex', position: 'relative'}}>

                <div style={{position: 'absolute', left: -6, top: 0, height: 36, width: 6, borderRadius: '4px 0px 0px 4px', background: disabled ? Colors.grey40 : isRetour ? Colors.retour : Colors[address.type]}} />

                <div style={{display: 'flex', width: '100%', borderBottom: `1px solid ${Colors.border}`}}>
                    <div
                        style={{display: 'flex', flex: 1, borderRight: `1px dotted ${Colors.border}`}}
                        onMouseEnter={() => setState({hover: true})}
                        onMouseLeave={() => setState({hover: false})}
                    >
                        <div style={{flex: 1, padding: 6}}>

                            <S1 style={{display: 'flex', alignItems: 'center'}}>
                                <i style={{marginRight: 6, fontSize: 16}} className='mdi mdi-map-marker' />
                                {`${address.street} ${address.nr}${address.addition}`}
                            </S1>

                            <P style={{marginLeft: 25}}>{address.street2}</P>
                            <P style={{marginLeft: 25, marginBottom: 12}}>{`${address.postalCode} ${address.city} (${address.country})`}</P>

                            {address.name &&
                                <P style={{display: 'flex', alignItems: 'center'}}>
                                    <i style={{marginRight: 6, fontSize: 16}} className={address.isBusiness ? 'mdi mdi-store' : address.attention ? 'mdi mdi-home' : 'mdi mdi-account'} />
                                    {address.name}
                                </P>
                            }

                            {address.attention &&
                                <P style={{display: 'flex', alignItems: 'center'}}>
                                    <i style={{marginRight: 6, fontSize: 16}} className='mdi mdi-account' />
                                    {address.attention}
                                </P>
                            }

                            {address.phone &&
                                <P style={{display: 'flex', alignItems: 'center'}}>
                                    <i style={{marginRight: 6, fontSize: 16}} className='mdi mdi-phone' />
                                    {address.phone}
                                </P>
                            }

                            {address.email &&
                                <P style={{display: 'flex', alignItems: 'center'}}>
                                    <i style={{marginRight: 6, fontSize: 16}} className='mdi mdi-at' />
                                    {address.email}
                                </P>
                            }

                        </div>

                        {onClickPrintLabels && address.type === 'delivery' && hover && !disabled &&
                            <div style={{height: 42, width: 42}}>
                                <IconButton
                                    onClick={() => onClickPrintLabels(order._id, address.index)}
                                    icon='mdi mdi-printer'
                                />
                            </div>
                        }

                        {onClickEdit && hover && !disabled &&
                            <div style={{height: 42, width: 42}}>
                                <IconButton
                                    icon='mdi mdi-pencil'
                                    onClick={() => onClickEdit(address.index)}
                                />
                            </div>
                        }
                    </div>

                    <div style={{display: 'flex', flexDirection: 'column', flex: 1}}>
                        <div style={{flex: 1, padding: 6}}>
                            <S1>{isRetour ? 'Retour' : address.type === 'pickup' ? 'Ophalen' : 'Bezorgen'}{disabled ? ' (originele adres)' : ''}</S1>

                            <P style={{display: 'flex', alignItems: 'center', marginBottom: 12}}>
                                <i style={{marginRight: 6, fontSize: 16}} className='mdi mdi-clock-outline' />
                                {`${address.startTime} - ${address.endTime}`}
                            </P>

                            <div style={{display: 'flex', alignItems: 'center'}}>
                                {address.gpsRequired &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-map-marker' />
                                }
                                {address.signatureRequired &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-fountain-pen-tip' />
                                }
                                {address.nameRequired && !address.signatureRequired &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-signature-text' />
                                }
                                {address.signatureNeighbors &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-home-edit-outline' />
                                }
                                {address.statedAddressOnly &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-home' />
                                }
                                {address.mailboxDelivery &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-mailbox-open-up' />
                                }
                                {address.refrigerated &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-snowflake' />
                                }
                                {address.scanRequired &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-barcode-scan' />
                                }
                                {address.imageRequired &&
                                    <Icon style={{marginRight: 6, fontSize: 20}} icon='mdi mdi-camera' />
                                }
                                {address.minimumAge &&
                                    <div style={{fontSize: 14, fontWeight: 500, marginRight: 6}}>{`${address.minimumAge}+`}</div>
                                }
                                {address.idCheck &&
                                    <Icon style={{fontSize: 20, marginRight: 3}} icon='mdi mdi-card-account-details-outline' />
                                }
                                {address.verificationCode &&
                                    <Icon style={{marginRight: 6, fontSize: 20}} icon='mdi mdi-shield-key-outline' />
                                }
                            </div>

                            {address.customOptions?.length > 0 && (
                                <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8, marginBottom: 8}}>
                                    {address.customOptions.map((optionId) => {
                                        const option = reseller.settings.orders.customOptions?.find((option) => option.id === optionId)

                                        if (!option) {
                                            return null
                                        }

                                        return (
                                            <Badge key={optionId} color={option.color}>
                                                {option.name}
                                            </Badge>
                                        )
                                    })}
                                </div>
                            )}

                            {address.instructions &&
                                <P style={{display: 'flex', whiteSpace: 'pre-line'}}>
                                    <Icon style={{marginRight: 6, fontSize: 16}} icon='mdi mdi-message-outline' />
                                    {address.instructions}
                                </P>
                            }
                        </div>
                    </div>
                </div>
            </div>

            {addressColli.map((collo, index) => {
                return (
                    <ViewOrderCollo
                        key={`collo${index}`}
                        order={order}
                        collo={collo}
                        reseller={reseller}
                        noBorder={index === addressColli.length - 1}
                        onClickPrintLabels={disabled ? undefined : onClickPrintLabels}
                    />
                )
            })}
        </Card>

    )
}

ViewOrderAddress.displayName = 'ViewOrderAddress'

export default ViewOrderAddress
